<template>
    <div class="app-rich-text-area" :class="{ 'shade-table-header-row': shadeTableHeaderRow }">
        <rich-text-area-toolbar
            v-if="editor"
            :editor="editor"
            :is-in-table-cell="isInTableCell"
            :is-in-table="isInTable"
            :current-cell-background="currentCellBackground"
            :current-border-color="currentBorderColor"
            @add-image="addImage"
            @set-cell-background="setCellBackground"
            @set-table-border-color="setTableBorderColor" />
        <div class="editor-wrapper" :class="{ 'drag-over': isDraggingOver }">
            <editor-content :editor="editor" class="editor-content" />
            <div v-if="isDraggingOver" class="drag-overlay">
                <div class="drag-message">
                    <v-icon large color="primary">
                        mdi-image-plus
                    </v-icon>
                    <div>Drop image here</div>
                </div>
            </div>
        </div>
        <input
            ref="fileInput"
            type="file"
            accept="image/*"
            style="display: none"
            @change="handleFileSelect">
    </div>
</template>

<script>
import { Editor, EditorContent } from '@tiptap/vue-2'
import RichTextAreaToolbar from '@/components/richTextArea/RichTextAreaToolbar.vue'
import { buildRichTextAreaExtensions } from '@/components/richTextArea/extensions'
import { uploadImage } from '@/services/fileService'

export default {
    name: 'AppRichTextArea',
    components: {
        EditorContent,
        RichTextAreaToolbar
    },
    props: {
        value: {
            type: String,
            default: ''
        },
        // Base URL for image upload (POST) and the resulting img src, e.g. '/api/Images'. Override to
        // point the editor at another endpoint (a future DeploymentSnapshotFile/DeploymentFile screen).
        imageEndpoint: {
            type: String,
            default: '/api/Images'
        },
        // Shades the first row of each table as a header, the way proposal PDFs print it.
        shadeTableHeaderRow: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            editor: null,
            isInTableCell: false,
            isInTable: false,
            currentCellBackground: '#ffffff',
            currentBorderColor: '#ced4da',
            isDraggingOver: false
        }
    },
    watch: {
        value(newValue) {
            if (!this.editor) {
                return
            }
            const currentTransformed = this.transformImageAlignment(this.editor.getHTML())
            if (currentTransformed === newValue) {
                return
            }
            this.editor.commands.setContent(this.reverseTransformImageAlignment(newValue), false)
        }
    },
    mounted() {
        this.editor = new Editor({
            extensions: buildRichTextAreaExtensions(),
            content: this.reverseTransformImageAlignment(this.value),
            editorProps: {
                handlePaste: (view, event) => this.handlePaste(event)
            },
            onUpdate: () => {
                const html = this.editor.getHTML()
                this.$emit('input', this.transformImageAlignment(html))
            },
            onSelectionUpdate: () => {
                this.updateTableState()
            }
        })

        this.updateTableState()
        this.setupDragAndDrop()
    },
    beforeDestroy() {
        this.removeDragAndDrop()
    },
    destroyed() {
        if (this.editor) {
            this.editor.destroy()
            this.editor = null
        }
    },
    methods: {
        insertText(text) {
            if (!this.editor) return
            this.editor.chain().focus().insertContent(text).run()
        },
        addImage() {
            this.$refs.fileInput.click()
        },
        handleFileSelect(event) {
            const file = event.target.files[0]
            if (file && file.type.startsWith('image/')) {
                this.insertImageFromFile(file)
            }
            event.target.value = ''
        },
        handlePaste(event) {
            const imageFiles = Array.from(event.clipboardData?.files ?? [])
                .filter(file => file.type.startsWith('image/'))
            if (imageFiles.length === 0) {
                // Let the editor handle non-image pastes (text, html, etc.) normally.
                return false
            }
            imageFiles.forEach(file => this.insertImageFromFile(file))
            // Returning true tells the editor we handled the paste, so it won't also
            // insert the image as an inline base64 data URI.
            return true
        },
        async insertImageFromFile(file) {
            try {
                const fileId = await uploadImage(file, this.imageEndpoint)
                this.editor.chain().focus().setImage({ src: `${this.imageEndpoint}/${fileId}` }).run()
            } catch {
                alert('The image could not be uploaded. Please try a different image.')
            }
        },
        setupDragAndDrop() {
            const editorElement = this.$el.querySelector('.ProseMirror')
            if (!editorElement) return

            this.handleDragOver = (e) => {
                e.preventDefault()
                e.stopPropagation()
            }

            this.handleDragEnter = (e) => {
                e.preventDefault()
                e.stopPropagation()
                if (this.hasImageFiles(e.dataTransfer)) {
                    this.isDraggingOver = true
                }
            }

            this.handleDragLeave = (e) => {
                e.preventDefault()
                e.stopPropagation()
                if (e.target === editorElement || e.target.classList.contains('editor-wrapper')) {
                    this.isDraggingOver = false
                }
            }

            this.handleDrop = (e) => {
                e.preventDefault()
                e.stopPropagation()
                this.isDraggingOver = false

                const files = Array.from(e.dataTransfer.files)
                const imageFiles = files.filter(file => file.type.startsWith('image/'))

                if (imageFiles.length > 0) {
                    imageFiles.forEach(file => {
                        this.insertImageFromFile(file)
                    })
                }
            }

            editorElement.addEventListener('dragover', this.handleDragOver)
            editorElement.addEventListener('dragenter', this.handleDragEnter)
            editorElement.addEventListener('dragleave', this.handleDragLeave)
            editorElement.addEventListener('drop', this.handleDrop)
        },
        removeDragAndDrop() {
            const editorElement = this.$el.querySelector('.ProseMirror')
            if (!editorElement) return

            editorElement.removeEventListener('dragover', this.handleDragOver)
            editorElement.removeEventListener('dragenter', this.handleDragEnter)
            editorElement.removeEventListener('dragleave', this.handleDragLeave)
            editorElement.removeEventListener('drop', this.handleDrop)
        },
        hasImageFiles(dataTransfer) {
            if (!dataTransfer || !dataTransfer.types) return false
            return Array.from(dataTransfer.types).includes('Files')
        },
        updateTableState() {
            this.isInTableCell = this.editor.isActive('tableCell') || this.editor.isActive('tableHeader')
            this.isInTable = this.editor.isActive('table')

            if (this.isInTableCell) {
                const attrs = this.editor.getAttributes('tableCell').backgroundColor ||
                    this.editor.getAttributes('tableHeader').backgroundColor
                this.currentCellBackground = attrs || '#ffffff'
            }

            if (this.isInTable) {
                const tableAttrs = this.editor.getAttributes('table')
                this.currentBorderColor = tableAttrs.borderColor || '#ced4da'
            }
        },
        setCellBackground(color) {
            if (this.editor.isActive('tableCell')) {
                this.editor.chain().focus().updateAttributes('tableCell', { backgroundColor: color }).run()
            } else if (this.editor.isActive('tableHeader')) {
                this.editor.chain().focus().updateAttributes('tableHeader', { backgroundColor: color }).run()
            }
            this.updateTableState()
        },
        setTableBorderColor(color) {
            this.editor.chain().focus().updateAttributes('table', { borderColor: color }).run()
            this.updateTableState()
        },
        reverseTransformImageAlignment(html) {
            const div = document.createElement('div')
            div.innerHTML = html

            const paragraphs = Array.from(div.querySelectorAll('p'))

            for (const p of paragraphs) {
                const img = p.querySelector('img')
                if (!img) continue
                if (img.hasAttribute('wrapperstyle')) continue

                const alignment = p.style.textAlign || 'left'

                let containerStyle = 'position: relative; margin: 0px auto 0px 0px;'
                if (alignment === 'center') {
                    containerStyle = 'position: relative; margin: 0px auto;'
                } else if (alignment === 'right') {
                    containerStyle = 'position: relative; margin: 0px 0px 0px auto;'
                }

                img.setAttribute('containerstyle', containerStyle)
                img.setAttribute('wrapperstyle', 'display: flex;')

                p.replaceWith(img.cloneNode(true))
            }

            return div.innerHTML
        },
        transformImageAlignment(html) {
            const div = document.createElement('div')
            div.innerHTML = html

            const images = Array.from(div.querySelectorAll('img[wrapperstyle]'))

            for (const img of images) {
                const containerStyle = img.getAttribute('containerstyle') || ''

                let alignment = 'left'
                if (
                    containerStyle.includes('margin: 0px auto;') ||
                    containerStyle.includes('margin: 0px auto 0px auto')
                ) {
                    alignment = 'center'
                } else if (containerStyle.includes('margin: 0px 0px 0px auto')) {
                    alignment = 'right'
                }

                img.removeAttribute('containerstyle')
                img.removeAttribute('wrapperstyle')

                const wrapper = document.createElement('p')
                wrapper.style.textAlign = alignment
                wrapper.appendChild(img.cloneNode(true))
                img.replaceWith(wrapper)
            }

            return div.innerHTML
        }
    }
}
</script>

<style scoped>
.app-rich-text-area {
    display: flex;
    flex-direction: column;
    border: 0.0625rem solid #e0e0e0;
    border-radius: 0.25rem;
    background: #ffffff;
    box-shadow: 0 0.0625rem 0.1875rem rgba(0, 0, 0, 0.06);
}

.toolbar {
    display: flex;
    gap: 0.5rem;
    padding: 0.75rem;
    border-bottom: 0.0625rem solid #e0e0e0;
    background: linear-gradient(to bottom, #fafafa, #f5f5f5);
    flex-wrap: wrap;
    align-items: center;
}

.toolbar-group {
    display: flex;
    gap: 0.25rem;
    align-items: center;
}

.separator {
    width: 0.0625rem;
    height: 1.75rem;
    background-color: #d0d0d0;
    margin: 0 0.25rem;
}

.color-picker-wrapper {
    position: relative;
    display: inline-flex;
    align-items: center;
}

.color-picker-wrapper.disabled {
    opacity: 0.5;
    pointer-events: none;
}

.color-input {
    position: absolute;
    left: 0;
    top: 0;
    width: 2rem;
    height: 2rem;
    opacity: 0;
    cursor: pointer;
}

.color-input:disabled {
    cursor: not-allowed;
}

.editor-wrapper {
    position: relative;
    flex-grow: 1;
    background: #ffffff;
    transition: background-color 0.2s;
}

.editor-wrapper.drag-over {
    background-color: #f0f7ff;
}

.editor-content {
    position: relative;
    z-index: 1;
}

.drag-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(25, 118, 210, 0.05);
    border: 0.125rem dashed #1976d2;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2;
    pointer-events: none;
}

.drag-message {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
    color: #1976d2;
    font-weight: 500;
}
</style>

<style>
.ProseMirror {
    min-height: 12.5rem;
    padding: 1rem;
    outline: none;
    font-size: 0.9375rem;
    line-height: 1.6;
    color: #333;
}

.ProseMirror p {
    margin-top: 0.7em;
    margin-bottom: 0.7em;
}

.ProseMirror p:first-child {
    margin-top: 0;
}

.ProseMirror p:last-child {
    margin-bottom: 0;
}

.ProseMirror ul,
.ProseMirror ol {
    padding-left: 1.5em;
    margin-top: 0.7em;
    margin-bottom: 0.7em;
}

.ProseMirror strong {
    font-weight: bold;
}

.ProseMirror em {
    font-style: italic;
}

.ProseMirror u {
    text-decoration: underline;
}

.ProseMirror s {
    text-decoration: line-through;
}

.ProseMirror a {
    color: #1976d2;
    text-decoration: underline;
    cursor: pointer;
}

.ProseMirror a:hover {
    color: #1565c0;
}

.ProseMirror img {
    max-width: 100%;
    height: auto;
    border-radius: 0.25rem;
}

.ProseMirror table {
    border-collapse: collapse;
    table-layout: fixed;
    width: 100%;
    margin: 0.7em 0;
    overflow: hidden;
    --table-border-color: #ced4da;
}

.ProseMirror td,
.ProseMirror th {
    min-width: 1em;
    border: 0.125rem solid var(--table-border-color);
    padding: 0.5rem 0.625rem;
    vertical-align: top;
    box-sizing: border-box;
    position: relative;
}

.ProseMirror th {
    text-align: left;
}

.shade-table-header-row .ProseMirror tr:first-child > td,
.shade-table-header-row .ProseMirror tr:first-child > th {
    background-color: #bfbfbf;
    font-weight: bold;
}

.ProseMirror .selectedCell:after {
    z-index: 2;
    position: absolute;
    content: "";
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    background: rgba(25, 118, 210, 0.15);
    pointer-events: none;
}

.ProseMirror .column-resize-handle {
    position: absolute;
    right: -0.125rem;
    top: 0;
    bottom: -0.125rem;
    width: 0.25rem;
    background-color: #1976d2;
    pointer-events: none;
}

.ProseMirror.resize-cursor {
    cursor: ew-resize;
    cursor: col-resize;
}
</style>
